import type { AmbientPresetId } from '~/boggle/ui/ambient/AmbientParticlesConfig'

/** Default shell particles preset for non-run routes (lobby, staging, catalog, etc.). */
export const BOGGLE_SHELL_AMBIENT_PAGE_DEFAULT_PRESET: AmbientPresetId = 'home'

/** Run ambient target: preset plus optional blend length (omit `transitionMs` for route default). */
export type BoggleShellAmbientScene = {
  preset: AmbientPresetId
  transitionMs?: number
}

/** Re-export for call sites that set shell ambient duration. */
export { BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS } from '~/boggle/ui/ambient/AmbientParticlesConfig'

/** Calm-play focus→active shell blend length as a fraction of the round timer. */
export const BOGGLE_SHELL_FOCUS_TO_ACTIVE_DURATION_FRACTION = 2 / 3

export function bogglePlayingCalmFocusToActiveBlendMs(intervalDurationMs: number): number {
  return Math.max(
    0,
    Math.round(intervalDurationMs * BOGGLE_SHELL_FOCUS_TO_ACTIVE_DURATION_FRACTION),
  )
}

export const boggleRunShellAmbient: Record<string, BoggleShellAmbientScene> = {
  playingGoldRush: {
    preset: 'gold-rush',
  },
  /** Last ~3s: blend length latched to remaining time in `useBoggleRunShellAmbientSync`. */
  playingEnd: {
    preset: 'end',
  },
  /** Pressure band (~10s–~3s): blend length latched so buildup reaches steady pressure when the final `playingEnd` phase begins (see hook). */
  playingPressure: {
    preset: 'pressure',
  },
  /** Prep countdown (`focus`). After play begins, sync layer switches to `active` over ~2/3 of the timer. */
  playingCalm: {
    preset: 'focus',
    transitionMs: 2000,
  },
  summaryHighScore: {
    preset: 'gold-rush',
  },
  summaryStandard: {
    preset: 'focus',
  },
}

/** Remaining time on the active interval; +∞ when there is no interval deadline. */
export function boggleIntervalRemainingMs(
  intervalDeadlineMs: null | number,
  timeMs: number,
): number {
  return intervalDeadlineMs === null
    ? Number.POSITIVE_INFINITY
    : Math.max(0, intervalDeadlineMs - timeMs)
}

/** Last ~10s of the round: pressure shell preset. */
export const RUN_PRESSURE_REMAINING_MS = 10_000
/** Last N ms of the round: blend pressure shell into the `end` preset until the timer expires. */
export const RUN_END_AMBIENT_REMAINING_MS = 3_000

export type BoggleAmbientPressureBand =
  | 'active'
  | 'calm'
  | 'end'
  | 'gold-rush'
  | 'pressure'
  | 'summary-new-best'
  | 'summary-standard'

export function getBoggleAmbientPressureBand(input: {
  goldRushActive: boolean
  intervalDeadlineMs: null | number
  intervalDurationMs: null | number
  intervalPlayBeginsAtMs: null | number
  showEndedSummary: boolean
  summaryStandardOutcome?: null | 'new-best' | 'tie'
  timeMs: number
}): BoggleAmbientPressureBand {
  if (input.showEndedSummary) {
    return input.summaryStandardOutcome === 'new-best'
      ? 'summary-new-best'
      : 'summary-standard'
  }

  if (input.goldRushActive) {
    return 'gold-rush'
  }

  const remaining = boggleIntervalRemainingMs(input.intervalDeadlineMs, input.timeMs)
  if (remaining <= RUN_END_AMBIENT_REMAINING_MS) {
    return 'end'
  }

  if (remaining <= RUN_PRESSURE_REMAINING_MS) {
    return 'pressure'
  }

  const inPrepCountdown =
    input.intervalPlayBeginsAtMs !== null && input.timeMs < input.intervalPlayBeginsAtMs
  if (
    inPrepCountdown ||
    input.intervalDurationMs === null ||
    input.intervalDurationMs <= 0
  ) {
    return 'calm'
  }

  return 'active'
}

/** Latched blend durations while the run shell crosses pressure/end bands. */
export type BoggleShellAmbientBandLatchRefs = {
  endBlendDurationLatchRef: { current: null | number }
  pressureBlendDurationLatchRef: { current: null | number }
}

export function resolveBoggleRunShellAmbientScene(input: {
  goldRushActive: boolean
  intervalDeadlineMs: null | number
  intervalDurationMs: null | number
  intervalPlayBeginsAtMs: null | number
  latches: BoggleShellAmbientBandLatchRefs
  showEndedSummary: boolean
  summaryStandardOutcome?: null | 'new-best' | 'tie'
  timeMs: number
}): BoggleShellAmbientScene {
  const band = getBoggleAmbientPressureBand(input)

  if (band === 'summary-new-best') {
    input.latches.pressureBlendDurationLatchRef.current = null
    input.latches.endBlendDurationLatchRef.current = null
    return boggleRunShellAmbient.summaryHighScore
  }

  if (band === 'summary-standard') {
    input.latches.pressureBlendDurationLatchRef.current = null
    input.latches.endBlendDurationLatchRef.current = null
    return boggleRunShellAmbient.summaryStandard
  }

  if (band === 'gold-rush') {
    input.latches.pressureBlendDurationLatchRef.current = null
    input.latches.endBlendDurationLatchRef.current = null
    return boggleRunShellAmbient.playingGoldRush
  }

  const remaining = boggleIntervalRemainingMs(input.intervalDeadlineMs, input.timeMs)

  if (band === 'end') {
    input.latches.pressureBlendDurationLatchRef.current = null
    if (input.latches.endBlendDurationLatchRef.current === null) {
      input.latches.endBlendDurationLatchRef.current = Math.max(1, Math.round(remaining))
    }
    return {
      preset: boggleRunShellAmbient.playingEnd.preset,
      transitionMs: input.latches.endBlendDurationLatchRef.current,
    }
  }

  input.latches.endBlendDurationLatchRef.current = null

  if (band === 'pressure') {
    if (input.latches.pressureBlendDurationLatchRef.current === null) {
      input.latches.pressureBlendDurationLatchRef.current = Math.max(
        1,
        Math.round(remaining - RUN_END_AMBIENT_REMAINING_MS),
      )
    }
    return {
      preset: boggleRunShellAmbient.playingPressure.preset,
      transitionMs: input.latches.pressureBlendDurationLatchRef.current,
    }
  }

  input.latches.pressureBlendDurationLatchRef.current = null

  if (band === 'calm') {
    return boggleRunShellAmbient.playingCalm
  }

  return {
    preset: 'active',
    transitionMs: bogglePlayingCalmFocusToActiveBlendMs(input.intervalDurationMs!),
  }
}

export function areBoggleShellAmbientScenesEqual(
  a: BoggleShellAmbientScene,
  b: BoggleShellAmbientScene | null,
): boolean {
  if (b === null) {
    return false
  }

  return a.preset === b.preset && Object.is(a.transitionMs, b.transitionMs)
}
